import { createConsumer } from '@rails/actioncable';

// NOTICE: KAPTION-INTEGRATION
// agent-presence.HEARTBEAT.1
// Was 20000, matching the server's former 20s presence TTL. A heartbeat at the
// same cadence had zero margin: a single delayed tick — routine in a
// backgrounded tab, since browsers throttle setTimeout there — lets the
// Redis presence entry expire and the user flips to "offline" until the next
// beat lands. 8s remains comfortably below the current 20s development and
// 90s production defaults in OnlineStatusTracker::PRESENCE_DURATION.
const PRESENCE_INTERVAL = 8000;
const RECONNECT_INTERVAL = 1000;
// NOTICE: KAPTION-INTEGRATION — Increase stale threshold so background tabs
// don't trigger a visible reconnection flash when the user returns. It only
// governs *missed pings*: a socket that actually closes is reopened by
// scheduleReopen (websocket.RECONNECT.1), not after this window.
const STALE_THRESHOLD = 30; // seconds (default is 6)
// NOTICE: KAPTION-INTEGRATION
// websocket.RECONNECT.2
// How long a lost connection may stay down before the "Disconnected" banner
// appears. Every production deploy restarts the servers holding the sockets;
// scheduleReopen brings a socket back within a couple of seconds, so a deploy
// never shows the banner — only a real outage does. Keep this below
// ReconnectService's 15s DISCONNECT_DELAY_THRESHOLD: that margin is what lets
// the catch-up fetch cover a gap whose disconnect is reported this late.
const DISCONNECT_DEBOUNCE = 10000; // ms
// NOTICE: KAPTION-INTEGRATION
// websocket.RECONNECT.1
// @rails/actioncable 6.1 reopens a closed socket only once its
// ConnectionMonitor finds the connection stale, which with STALE_THRESHOLD at
// 30s left every dropped socket down for ~30s (measured: 30.4s). Reopen
// promptly instead, with jitter so a restarted server isn't hit by all of its
// clients at once, backing off while the server stays unreachable.
const FIRST_REOPEN_MIN_DELAY = 500; // ms
const FIRST_REOPEN_MAX_DELAY = 2000; // ms
const MAX_REOPEN_DELAY = 15000; // ms

export function reopenDelay(attempt, random = Math.random()) {
  if (attempt === 0) {
    return (
      FIRST_REOPEN_MIN_DELAY +
      random * (FIRST_REOPEN_MAX_DELAY - FIRST_REOPEN_MIN_DELAY)
    );
  }
  const base = Math.min(MAX_REOPEN_DELAY, 1000 * 2 ** attempt);
  return base * (0.5 + random * 0.5);
}

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);

    // Increase tolerance for missed pings (default: 6s = 2 missed pings)
    const { monitor } = this.consumer.connection;
    monitor.constructor.staleThreshold = STALE_THRESHOLD;

    this.disconnectDebounceTimer = null;
    this.reopenTimer = null;
    this.reopenAttempts = 0;
    this.setupVisibilityReconnect();

    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        // `willAttemptReconnect` is false when the app disconnected on purpose
        // (sign-out) or the server refused reconnection (e.g. unauthorized).
        disconnected: ({ willAttemptReconnect = true } = {}) => {
          BaseActionCableConnector.isDisconnected = true;
          this.initReconnectTimer();
          if (willAttemptReconnect) this.scheduleReopen();
          // Debounce the UI notification so short reconnections are invisible
          clearTimeout(this.disconnectDebounceTimer);
          this.disconnectDebounceTimer = setTimeout(() => {
            if (BaseActionCableConnector.isDisconnected) {
              this.onDisconnected();
            }
          }, DISCONNECT_DEBOUNCE);
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    this.triggerPresenceInterval();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.clearReopenTimer();
      clearTimeout(this.disconnectDebounceTimer);
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // websocket.RECONNECT.1 — see the constants above. Keeps trying until
  // checkConnection sees the socket open again and clears the timer.
  scheduleReopen = () => {
    if (this.reopenTimer) return;
    this.reopenTimer = setTimeout(() => {
      this.reopenTimer = null;
      const { connection } = this.consumer;
      if (connection.isOpen()) return;
      // A socket that is still connecting gets time to finish.
      if (!connection.isActive?.()) {
        this.reopenAttempts += 1;
        connection.reopen();
      }
      this.scheduleReopen();
    }, reopenDelay(this.reopenAttempts));
  };

  clearReopenTimer = () => {
    clearTimeout(this.reopenTimer);
    this.reopenTimer = null;
    this.reopenAttempts = 0;
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  setupVisibilityReconnect() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      // Proactively reopen on tab return — don't wait for ConnectionMonitor poll
      const { connection } = this.consumer;
      if (!connection.isOpen()) {
        connection.reopen();
      }
      // NOTICE: KAPTION-INTEGRATION
      // agent-presence.HEARTBEAT.2
      // Refresh presence immediately on tab
      // return instead of waiting for the next PRESENCE_INTERVAL tick. A tab
      // backgrounded long enough (Chrome's intensive timer throttling) can
      // lag the heartbeat well past the server's presence TTL even at 8s, so
      // without this a user could still show "offline" for a beat after
      // switching back.
      this.subscription.updatePresence();
    });
  }

  disconnect() {
    this.clearReopenTimer();
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
